import { useLayoutEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBug,
  faExclamationTriangle,
  faLock,
  faMagnifyingGlassPlay,
  faPaperPlane,
  faPlus,
  faSpinner,
  faTimes,
} from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { ChatMessage } from '@/components/chat/ChatMessage';
import GeneratingResponseBubble from '@/components/chat/GeneratingResponseBubble';
import { useVideoFinder } from '@/contexts/VideoFinderContext';

// Beispiel-Anfragen für den leeren Zustand - ein Klick sendet die Anfrage direkt ab
const EXAMPLE_QUERIES: readonly string[] = [
  'Wie funktioniert die Kernstrategie?',
  'Zeig mir Videos über Cash-Secured Puts',
  'Wo wird das Rollen von Optionen erklärt?',
  'Was muss ich vor Earnings beachten?',
];

const TEXTAREA_MAX_HEIGHT_PX = 200;

export default function VideoFinderPanel() {
  const { isAdmin } = useUser();
  const {
    session,
    messages,
    status,
    sending,
    backgroundError,
    clearBackgroundError,
    debugMode,
    setDebugMode,
    startNewSearch,
    sendMessage,
    draft,
    setDraft,
  } = useVideoFinder();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const llmActive = session?.llmActive === true;
  const isReady = status === 'ready';
  const canSend = isReady && !sending && !llmActive;

  // Bei neuen Nachrichten bzw. wenn die Antwort-Bubble erscheint/verschwindet nach unten scrollen.
  // Das Panel wird bei jedem Öffnen neu gemountet, der Ref startet also jedes Mal auf false -
  // beim ersten Lauf wird deshalb hart gesprungen statt einen langen Verlauf sichtbar
  // durchzuscrollen. Layout-Effekt, damit im ersten Frame nicht der Anfang aufblitzt.
  const hasScrolledRef = useRef(false);
  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: hasScrolledRef.current ? 'smooth' : 'auto',
      block: 'nearest',
    });
    hasScrolledRef.current = true;
  }, [messages.length, llmActive]);

  // Der Entwurf überlebt das Schließen, die imperativ gesetzte Höhe der Textarea aber nicht -
  // deshalb beim Öffnen einmal nachziehen und fokussieren. Der Composer existiert erst ab
  // status === 'ready', vorher ist der Ref null.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (textarea === null) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
    textarea.focus();
  }, [isReady]);

  const resetTextareaHeight = () => {
    if (textareaRef.current !== null) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleSend = async (text: string) => {
    const content = text.trim();
    if (content.length === 0 || !canSend) {
      return;
    }

    setDraft('');
    resetTextareaHeight();

    const ok = await sendMessage(content);
    if (!ok) {
      setDraft(content);
    }

    textareaRef.current?.focus();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSend(draft);
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend(draft);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(e.target.value);

    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
  };

  const handleNewSearch = () => {
    setDraft('');
    resetTextareaHeight();
    void startNewSearch();
  };

  const newSearchDisabled = status === 'creating' || sending;

  return (
    // Exakt so hoch wie der Content-Bereich der Modal-Komponente (deren maxHeight "90dvh" minus
    // ihrem fixen Header-Abzug von 100px) - sonst gibt es zwei Scrollbars.
    <div className="flex flex-col h-[calc(90dvh-100px)]">
      {/* Toolbar - Icon, Titel und Schließen-Button kommen vom Modal-Header darüber */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0">
        <p className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 truncate">
          Beschreibe, was du lernen möchtest – ich finde die passenden Academy-Videos.
        </p>

        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {isAdmin && (
            <button
              type="button"
              onClick={() => setDebugMode(!debugMode)}
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
                debugMode
                  ? 'text-white bg-orange-600 hover:bg-orange-700'
                  : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
              }`}
              title={debugMode ? 'Debug Mode deaktivieren' : 'Debug Mode aktivieren'}
            >
              <FontAwesomeIcon icon={faBug} className="w-3 h-3 mr-1" />
              DEBUG
            </button>
          )}

          <button
            type="button"
            onClick={handleNewSearch}
            disabled={newSearchDisabled}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title="Neue Suche starten"
          >
            <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5 mr-1.5" />
            Neue Suche
          </button>
        </div>
      </div>

      {/* Hintergrund-Fehler beim Polling */}
      {backgroundError !== null && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-2 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm text-yellow-800 dark:text-yellow-200">{backgroundError}</span>
          </div>
          <button
            type="button"
            onClick={clearBackgroundError}
            className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 transition-colors cursor-pointer"
            title="Hinweis ausblenden"
          >
            <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Suche beendet / abgelaufen */}
      {(status === 'closed' || status === 'expired') && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800 px-4 py-3 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faLock} className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">
              {status === 'closed' ? 'Diese Suche wurde beendet.' : 'Diese Suche ist abgelaufen.'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleNewSearch}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors cursor-pointer"
          >
            <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5 mr-1.5" />
            Neue Suche starten
          </button>
        </div>
      )}

      {/* Session konnte nicht angelegt werden */}
      {status === 'createError' && (
        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 py-3 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span className="text-sm text-red-800 dark:text-red-200 font-medium">
              Die Suche konnte nicht gestartet werden.
            </span>
          </div>
          <button
            type="button"
            onClick={handleNewSearch}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors cursor-pointer"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Nachrichten - overscroll-contain, damit am Listenende nicht die Seite hinter dem Modal scrollt */}
      <div className="flex-1 overflow-y-auto overscroll-contain p-4 min-h-0">
        {status === 'creating' ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 animate-spin mb-3 text-blue-600 dark:text-blue-400" />
            <p className="text-sm">Suche wird vorbereitet…</p>
          </div>
        ) : messages.length === 0 && status === 'ready' ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-2xl flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faMagnifyingGlassPlay} className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            {/* h3, weil das h2 in diesem Dialog dem Modal-Header gehört */}
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Wonach suchst du?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6">
              Beschreibe ein Thema, eine Strategie oder eine Frage – der Video-Finder zeigt dir die
              Academy-Videos, in denen du die Antwort findest.
            </p>
            <div className="flex flex-wrap justify-center gap-2 max-w-2xl">
              {EXAMPLE_QUERIES.map((query) => (
                <button
                  key={query}
                  type="button"
                  onClick={() => { void handleSend(query); }}
                  disabled={!canSend}
                  className="px-3 py-1.5 text-sm rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {query}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} debugMode={debugMode} />
            ))}
            {llmActive && <GeneratingResponseBubble />}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer - nur solange die Suche offen ist */}
      {isReady && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0">
          <form onSubmit={handleSubmit} className="flex items-start gap-3">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={handleTextareaChange}
              onKeyDown={handleTextareaKeyDown}
              placeholder={llmActive ? 'Warte auf die Antwort…' : 'Was möchtest du lernen?'}
              disabled={sending}
              rows={1}
              className="flex-1 w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-800"
              style={{ minHeight: '48px', maxHeight: `${TEXTAREA_MAX_HEIGHT_PX}px` }}
            />
            <button
              type="submit"
              disabled={draft.trim().length === 0 || !canSend}
              className="bg-blue-600 text-white rounded-lg px-4 py-3 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors flex-shrink-0"
              title="Suche absenden"
            >
              <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} className={`w-5 h-5 ${sending ? 'animate-spin' : ''}`} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
